import {
    ApiRef,
    DiscoveryApi,
    IdentityApi,
    createApiRef,
} from '@backstage/core-plugin-api';
import {
    C4DiagramEntity,
    MergeCommands,
} from '@internal/plugin-rtl-data/src/components/cards/C4DiagramCard/types';

/**
 * The {@link ApiRef} of {@link DiscoveryApi}.
 *
 * @public
 */
export const modelApiRef: ApiRef<ModelApi> = createApiRef({
    id: 'rtldata.model',
});

export type cmdbGroupType = 'technical' | 'custom';

export type GroupEditRequest = {
    cmdbTeamUID: string;
    cmdbTeamShortId: string;
    cmdbGroupName: string;
    cmdbGroupType: cmdbGroupType;
    members: string[];
    requestMessage: string;
    requestingUser: string;
};

/**
 * A change to an Entra group of a department. Unlike a team, which is
 * addressed by its uid and its short id, a department is addressed by its vuid
 * alone - that is what names its directory in the CMDB repository and what
 * keys it inside the file.
 *
 * `members` are the `@netrtl.com` accounts the department *declares*. The
 * members it derives from its contacts are not among them and must not be:
 * writing them back would turn today's contacts into declared members, and one
 * dropped from the contacts later would stay in the group for good.
 */
export type DepartmentGroupEditRequest = {
    cmdbDepartmentVuid: string;
    cmdbGroupName: string;
    cmdbGroupType: cmdbGroupType;
    members: string[];
    requestMessage: string;
    requestingUser: string;
};

/**
 * A change to an Entra group of a product.
 *
 * No `cmdbGroupType`: a product only has the groups it asked for itself, so
 * `custom` is the one category there is and the client states it in the path.
 *
 * Three keys rather than the two a team needs - the uid of the team names the
 * directory the product directory sits in, the short key of the product names
 * that directory, and the short id of the team keys it inside the file.
 */
export type ProductGroupEditRequest = {
    cmdbTeamUID: string;
    cmdbTeamShortId: string;
    cmdbProductKey: string;
    cmdbGroupName: string;
    members: string[];
    requestMessage: string;
    requestingUser: string;
};

export type namespaceUpdate = {
    clusterUid: string;
    namespaces: string[];
    environment: string;
};

export type NamespaceEditRequest = {
    provider: string;
    namespaceUpdate: namespaceUpdate[];
    requestingUser: string;
    clusterGroup: string;
    productName: string;
    teamCmdbKey: string;
    teamUID: string;
};

export interface ModelApi {
    update(model: any): void;
    requestAdGroupUpdate(editRequest: GroupEditRequest): Promise<any>;
    requestDepartmentAdGroupUpdate(
        editRequest: DepartmentGroupEditRequest,
    ): Promise<any>;
    requestProductAdGroupUpdate(
        editRequest: ProductGroupEditRequest,
    ): Promise<any>;
    requestNamespaceUpdate(editRequest: NamespaceEditRequest): Promise<any>;
    postMergeCommands(mergeCommands: MergeCommands): Promise<string[]>;
    getSystemProductComponents(
        namespace: string | undefined,
        system: string,
    ): Promise<C4DiagramEntity[]>;
    getRelatedEntities(
        kind: string,
        namepace: string | undefined,
        name: string,
    ): Promise<C4DiagramEntity[]>;
}

export class ModelClient implements ModelApi {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
    topic: string | undefined;
    requested: string[] = [];

    constructor(identityApi: IdentityApi, discoveryApi: DiscoveryApi) {
        this.discoveryApi = discoveryApi;
        this.identityApi = identityApi;
    }

    async postMergeCommands(mergeCommands: MergeCommands): Promise<string[]> {
        return await (
            await this.call('POST', 'commands', JSON.stringify(mergeCommands))
        ).json();
    }

    async requestNamespaceUpdate(
        editRequest: NamespaceEditRequest,
    ): Promise<any> {
        const body = JSON.stringify({
            namespaceUpdate: editRequest.namespaceUpdate,
            teamCmdbKey: editRequest.teamCmdbKey,
            requestingUser: editRequest.requestingUser,
            cloudProvider: editRequest.provider,
        });
        return (
            await this.call(
                'POST',
                `teams/${editRequest.teamUID}/products/${editRequest.productName}/clustergroup/${editRequest.clusterGroup}/namespaces/update`,
                body,
            )
        ).json();
    }
    async requestAdGroupUpdate(editRequest: GroupEditRequest): Promise<any> {
        const body = JSON.stringify({
            team_short_id: editRequest.cmdbTeamShortId,
            members: editRequest.members,
            requesting_user: editRequest.requestingUser,
            request_message: editRequest.requestMessage,
        });
        return (
            await this.call(
                'POST',
                `teams/${editRequest.cmdbTeamUID}/adgroups/${editRequest.cmdbGroupType}/${editRequest.cmdbGroupName}/members`,
                body,
            )
        ).json();
    }

    async requestDepartmentAdGroupUpdate(
        editRequest: DepartmentGroupEditRequest,
    ): Promise<any> {
        const body = JSON.stringify({
            members: editRequest.members,
            requesting_user: editRequest.requestingUser,
            request_message: editRequest.requestMessage,
        });
        return (
            await this.call(
                'POST',
                `departments/${editRequest.cmdbDepartmentVuid}/adgroups/${editRequest.cmdbGroupType}/${editRequest.cmdbGroupName}/members`,
                body,
            )
        ).json();
    }

    async requestProductAdGroupUpdate(
        editRequest: ProductGroupEditRequest,
    ): Promise<any> {
        const body = JSON.stringify({
            team_short_id: editRequest.cmdbTeamShortId,
            members: editRequest.members,
            requesting_user: editRequest.requestingUser,
            request_message: editRequest.requestMessage,
        });
        return (
            await this.call(
                'POST',
                `teams/${editRequest.cmdbTeamUID}/products/${editRequest.cmdbProductKey}/adgroups/custom/${editRequest.cmdbGroupName}/members`,
                body,
            )
        ).json();
    }

    async update(model: any) {
        await this.call('PUT', 'model', JSON.stringify(model));
    }

    async getSystemProductComponents(
        namespace: string = 'default',
        system: string,
    ): Promise<C4DiagramEntity[]> {
        return (
            await this.call('GET', `components/${namespace}/${system}`)
        ).json();
    }

    async getRelatedEntities(
        kind: string,
        namespace: string = 'default',
        name: string,
    ): Promise<C4DiagramEntity[]> {
        return (
            await this.call('GET', `relations/${kind}/${namespace}/${name}`)
        ).json();
    }

    async call(method: string, path: string, body?: string): Promise<Response> {
        const result = await fetch(
            `${await this.discoveryApi.getBaseUrl('rtlDataPlugin')}/${path}`,
            {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${(await this.identityApi.getCredentials()).token}`,
                },
                body: body,
            },
        );
        return result;
    }
}
